import {Component, Input, OnInit} from '@angular/core';
import {Contenitore} from '../../../_models/Contenitore';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {MovimentiService} from '../../../_services/movimenti.service';
import {ModalController} from '@ionic/angular';
import {SaveMessage} from '../../../_helpers/save-message';
import {MessageConfirm} from "../../../_helpers/message-confirm.service";
import {Movimento} from 'src/app/_models/Movimento';
import {constants} from "../../../_helpers/constants";
import {AuthenticationService} from "../../../_services/authentication.service";
import {ReportService} from "../../../_services/report.service";
import {validaCodiceSuFormati} from '../../../_helpers/codice-contenitore-formato';

const MESSAGGIO_ERRORE_SALVATAGGIO = 'Ci sono problemi con il salvataggio.';

const emptyContenitore: Contenitore = {
  organizzazioneId: 0,
  id: 0,
  codice: '',
  descrizione: '',
  isActive: true,
  formato: 0,
  tipoBox: 0,
  giacenza: 0,
  giacenzaUdM2: 0
};

@Component({
  selector: 'app-contenitori-create',
  templateUrl: './contenitori-create.component.html',
  styleUrls: ['./contenitori-create.component.scss'],
  standalone: false
})
export class ContenitoriCreateComponent implements OnInit {
  @Input() contenitore: Contenitore;
  activeCreateMovimento = false;
  isNew = true;
  loading = false;
  protected readonly constants = constants;

  constructor(
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private movimentiService: MovimentiService,
    private reportService: ReportService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private deleteConfirm: MessageConfirm) { }

  ngOnInit() {
    if (this.contenitore == null) {
      this.contenitore = emptyContenitore;
      this.contenitore.organizzazioneId = this.organizzazioniService.organizzazioneId;
    }
  }

  async confirm() {
    if (this.isNew && !(await this.codiceValido())) {
      return;
    }
    this.loading = true;
    if (this.isNew) {
      return this.movimentiService.createContenitoreAsync(this.contenitore)
        .then(this.succesSave)
        .catch(this.errorSave)
        .finally(() => this.loading = false);
    }
    this.contenitore.executedBy = this.authService.currentUserValue.userName;
    return this.movimentiService.updateContenitoreAsync(this.contenitore)
      .then(this.succesSave)
      .catch(this.errorSave)
      .finally(() => this.loading = false);
  }

  // Issue #3594: il codice digitato dall'amministratore deve rispettare il default o uno dei formati
  // dell'organizzazione (il formato selezionato serve solo al messaggio). Formati non caricabili →
  // validazione saltata, resta la protezione del server.
  private async codiceValido(): Promise<boolean> {
    const codice = this.contenitore.codice;
    if (!codice) return true;
    const formati = await this.movimentiService
      .getContenitoriFormatiCachedAsync(this.organizzazioniService.organizzazioneId)
      .catch(() => null);
    if (!formati) return true;
    const formatoCampo = formati.find(f => f.id === this.contenitore.formato) ?? null;
    const esito = validaCodiceSuFormati(codice, formati, formatoCampo);
    if (!esito.valido) this.saveMessage.messageError(esito.messaggio);
    return esito.valido;
  }

  succesSave = (_contenitore: Contenitore) => {
    // Il backend risponde 200 con hasErrors nel corpo (es. codice non valido): modal non chiusa.
    if (_contenitore?.hasErrors) {
      this.saveMessage.messageError(_contenitore.errorMessage ?? MESSAGGIO_ERRORE_SALVATAGGIO);
      return;
    }
    if (this.isNew) {
      this.saveMessage.entityCreated('contenitore', _contenitore);
    } else {
      this.saveMessage.entityUpdated('contenitore', _contenitore);
    }
    if (this.activeCreateMovimento && (_contenitore.giacenza != 0 || _contenitore.id > 0)) {
      this.createMovimentoGiacenza(_contenitore);
    } else {
      void this.modalCtrl.dismiss(_contenitore, 'confirm');
    }
  };

  errorSave = () => this.saveMessage.messageError(MESSAGGIO_ERRORE_SALVATAGGIO);

  createMovimentoGiacenza = (contenitore: Contenitore) => {
    const dataEvento = new Date();
    const movimento: Movimento = {
      organizzazioneId: this.organizzazioniService.organizzazioneId,
      id: 0,
      dataUnixRegistrazione: Math.floor(dataEvento.getTime() / 1000),
      dataRegistrazione: dataEvento,
      rifOdP: contenitore.rifOdP,
      idRigaMateriale: 0,
      codArt: contenitore.codArt,
      unitaMisura: contenitore.udM,
      magUdM: contenitore.magUdM,
      quantita: contenitore.giacenza,
      quantitaUdM2: contenitore.giacenzaUdM2,
      coeffX1: 1,
      codLottoAlfa: contenitore.codLottoAlfa,
      codMagazzinoIn: contenitore.codMagazzino,
      codMagAreaIn: contenitore.codMagArea,
      codBox: contenitore.codice,
      contesto: 0,
      tipoMovimento: 0,
      tipoQuantita: contenitore.tipoBox,
      currentGiacenza: contenitore.giacenza,
      currentGiacenzaUdM2: contenitore.giacenzaUdM2,
      udM2: contenitore.udM2,
      saldaRiga: false,
      tara: contenitore.tara,
      executedBy: this.authService.currentUserValue.userName
    }
    this.movimentiService.createMovimentoAsync(movimento)
      .then((_movimento: Movimento) => {
        void this.modalCtrl.dismiss(contenitore, 'confirm');
      })
      .catch(() => this.saveMessage.messageError("Errore durante la creazione del movimento."))
      .finally(() => this.loading = false);
  };

  emptyContenitore = (contenitore: Contenitore) => {
    void this.deleteConfirm
      .presentAlert(`Sei sicuro di voler svuotare ${contenitore.codice}?`, () => {
        this.movimentiService.emptyContenitoreAsync(this.organizzazioniService.organizzazioneId, contenitore.codice)
          .then((_contenitore: Contenitore) => {
            this.saveMessage.entityEmptied('contenitore', _contenitore);
            void this.modalCtrl.dismiss(this.contenitore, 'confirm');
          });
      });
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  printContenitore(contenitore: Contenitore) {
    this.loading = true;
    // Scarica il PDF (formato pagina corretto in stampa) invece di aprirlo nel viewer del browser.
    // Con responseType 'blob' il body del 400 è un Blob: mostriamo il messaggio d'errore generico
    // esistente senza parsarlo.
    this.movimentiService.getReportContenitoreFile(this.organizzazioniService.organizzazioneId,
      contenitore.codice, '', 1)
      .then(blob => this.reportService.downloadPdf(blob, contenitore.codice))
      .catch(() => this.saveMessage.messageError("Ci sono problemi con il report del contenitore."))
      .finally(() => this.loading = false);
  }

  round(num: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
  }
}
